import { getContractWarnings } from '@/components/AlertNotification/services'
import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    ContractWarning,
    WarningItem,
} from '@/modules/Util/components/Alerts/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { AlertTriangle, ArrowRight, Clock, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'

const ContractAlertsWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    // Fetch Contract warnings
    const { data: contractData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACT_WARNINGS],
        queryFn: getContractWarnings,
    })

    // Process Contract data
    const contractWarnings = contractData?.results || []
    const contractExpired = contractWarnings.filter(
        (w) => w.type === 'contract_expired'
    )
    const contractExpiring = contractWarnings.filter(
        (w) => w.type === 'contract_expiring'
    )

    const handleContractClick = (contractId: number) => {
        navigate(
            generatePathDatacentral(paths.contractDetail, { id: contractId })
        )
    }

    const handleViewAll = () => {
        navigate(generatePathDatacentral(paths.customerContractList, {}))
    }

    const renderWarningItem = (warning: WarningItem) => {
        const contractDataItem = warning.data as ContractWarning
        const isExpired = warning.type === 'contract_expired'

        return (
            <div
                key={warning.id}
                onClick={() =>
                    handleContractClick(contractDataItem.contract_id)
                }
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                        className={`p-1.5 rounded-full flex-shrink-0 ${isExpired ? 'bg-red-100' : 'bg-orange-100'}`}
                    >
                        <FileText
                            className={`w-3.5 h-3.5 ${isExpired ? 'text-red-600' : 'text-orange-600'}`}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {contractDataItem.contract_number_id}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {contractDataItem.customer_name}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            isExpired
                                ? 'bg-red-100 text-red-700'
                                : 'bg-orange-100 text-orange-700'
                        }`}
                    >
                        {isExpired
                            ? Math.abs(contractDataItem.days_overdue)
                            : contractDataItem.days_to_expiry}
                        d
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('alerts.contracts')}
                    </Typogaphy>
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        <div className="grid grid-cols-2 gap-2">
                            <Skeleton className="h-16 rounded-lg" />
                            <Skeleton className="h-16 rounded-lg" />
                        </div>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div
                            className="grid grid-cols-2 gap-2 flex-shrink-0 mb-3"
                            data-tour="contract-alerts-summary"
                        >
                            <div
                                className="flex items-center gap-2 p-2.5 bg-red-50 rounded-lg"
                                data-tour="contract-expired"
                            >
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                                <div>
                                    <div className="text-lg font-bold text-red-600">
                                        {contractExpired.length}
                                    </div>
                                    <div className="text-[10px] text-red-600">
                                        {t('alerts.expired')}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="flex items-center gap-2 p-2.5 bg-orange-50 rounded-lg"
                                data-tour="contract-expiring"
                            >
                                <Clock className="w-4 h-4 text-orange-600" />
                                <div>
                                    <div className="text-lg font-bold text-orange-600">
                                        {contractExpiring.length}
                                    </div>
                                    <div className="text-[10px] text-orange-600">
                                        {t('alerts.expiring')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 flex-1">
                            {contractWarnings.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t('alerts.no_alerts')}
                                </div>
                            ) : (
                                <>
                                    {contractWarnings
                                        .slice(0, 5)
                                        .map((warning) =>
                                            renderWarningItem(warning)
                                        )}
                                    {/* Empty placeholders */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            5 - contractWarnings.length
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                                        />
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All */}
                        {contractWarnings.length > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} (
                                    {contractWarnings.length})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default ContractAlertsWidget
